import type { AxiosPromise, AxiosRequestConfig, Method } from 'axios'
import axios, { AxiosRequestHeaders } from 'axios'
import { ElLoading, ElMessageBox, ElNotification, LoadingOptions } from 'element-plus'
import { useAdminInfo } from '/@/stores/adminInfo'
import { useAbortController, AxiosRequestConfigWithSignal } from '/@/hooks/useAbortController'
import { i18n } from '/@/lang'
import { downloadFileStream } from '/@/utils/common'
import { downloadFile } from '/@/api/common'

interface AxiosRequestHeadersWithToken extends AxiosRequestHeaders {
  token?: string
}

window.requests = []
window.tokenRefreshing = false
window.tokenExpiresVisible = false
const loadingInstance: LoadingInstance = {
  target: null,
  count: 0
}

/*
 * 根据运行环境获取基础请求URL
 */
export const getUrl = (): string => {
  return import.meta.env.VITE_AXIOS_BASE_URL as string
}

export const handleAuthError = () => {
  const adminInfo = useAdminInfo()
  if (!window.tokenExpiresVisible) {
    window.tokenExpiresVisible = true
    ElMessageBox.confirm('登录状态已过期，您可以继续留在该页面，或者重新登录', '系统提示', {
      confirmButtonText: '重新登录',
      cancelButtonText: '取消',
      type: 'warning'
    })
      .then(() => {
        adminInfo.removeToken().then(() => {
          window.tokenExpiresVisible = false
          location.href = '/login'
        })
      })
      .catch(() => {
        window.tokenExpiresVisible = false
      })
  }
}

/*
 * 创建Axios
 * 默认开启`reductDataFormat(简洁响应)`,返回类型为`ApiPromise`
 * 关闭`reductDataFormat`,返回类型则为`AxiosPromise`
 */
function createAxios (
  axiosConfig: AxiosRequestConfig,
  options: Options = {},
  loading: LoadingOptions = {}
): ApiPromise | AxiosPromise | Promise<Blob> {
  const adminInfo = useAdminInfo()
  const abortController = useAbortController()
  const Axios = axios.create({
    baseURL: getUrl(),
    timeout: 6000 * 10 * 5,
    responseType: 'json',
    headers: {
      channel: 'web',
    }
  })

  options = Object.assign(
    {
      cancelDuplicateRequest: true, // 是否开启取消重复请求, 默认为 true
      loading: false, // 是否开启loading层效果, 默认为false
      reductDataFormat: true, // 是否开启简洁的数据结构响应, 默认为true
      showErrorMessage: true, // 是否开启接口错误信息展示,默认为true
      showCodeMessage: true, // 是否开启code不为1时的信息提示, 默认为true
      showSuccessMessage: false, // 是否开启code为1时的信息提示, 默认为false
      anotherToken: '' // 当前请求使用另外的用户token
    },
    options
  )

  // 请求拦截
  Axios.interceptors.request.use(
    config => {
      options.cancelDuplicateRequest && abortController.removePending(<AxiosRequestConfigWithSignal>config)
      const { requestIdInfo } = abortController.addPending(<AxiosRequestConfigWithSignal>config)
      // 创建loading实例
      if (options.loading) {
        loadingInstance.count++
        if (loadingInstance.count === 1) {
          loadingInstance.target = ElLoading.service(loading)
        }
      }
      // 自动携带token
      if (config.headers) {
        const token = adminInfo.getToken()
        if (token) (config.headers as AxiosRequestHeadersWithToken)['token'] = token

        config.headers['content-type'] = 'application/json; charset=UTF-8'

        requestIdInfo?.new && (config.headers['x-request-id'] = requestIdInfo?.new)


        // Hack 处理 Axios 非 POST请求 data 不存在，自定义 ContentType 无法覆盖默认配置
        if (!config.data) config.data = { }
      }

      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

  // 响应拦截
  Axios.interceptors.response.use(
    response => {
      abortController.removePending(<AxiosRequestConfigWithSignal>response.config)
      options.loading && closeLoading(options) // 关闭loading
      // 二进制数据则直接返回
      if (response.request.responseType === 'blob' || response.request.responseType === 'arraybuffer') {
        return options.reductDataFormat ? response.data : response
      }
      // Token 过期处理
      if (response.data.code === 401) {
        handleAuthError()
        return Promise.reject(response.data.msg)
      } else if (response.data.code !== 200) {
        options.showErrorMessage && httpErrorStatusHandle({ response: { status: response.data.code, msg: response.data.msg } })
        return Promise.reject(response.data)
      }
      return options.reductDataFormat ? response.data : response
    },
    error => {
      error.config && abortController.removePending(<AxiosRequestConfigWithSignal>error.config)
      options.loading && closeLoading(options) // 关闭loading
      options.showErrorMessage && httpErrorStatusHandle(error) // 处理错误状态码
      return Promise.reject(error) // 错误继续返回给到具体页面
    }
  )
  return options.reductDataFormat ? (Axios(axiosConfig) as ApiPromise) : (Axios(axiosConfig) as AxiosPromise)
}

export default createAxios

/**
 * 处理异常
 * @param {*} error
 */
function httpErrorStatusHandle (error: any) {
  // 处理被取消的请求
  if (axios.isCancel(error))
    return console.error(i18n.global.t('axios.Automatic cancellation due to duplicate request:') + error.message)
  let message = ''
  if (error && error.response) {
    switch (error.response.status) {
      case 302:
        message = i18n.global.t('axios.Interface redirected!')
        break
      case 400:
        message = i18n.global.t('axios.Incorrect parameter!')
        break
      case 401:
        // message = i18n.global.t('axios.You do not have permission to operate!')
        // 401 不提示
        message = ''
        handleAuthError()
        // console.log(401)
        // Local.remove(ADMIN_INFO)
        // setTimeout(() => {
        //   router.push({ path: '/login' })
        // }, 2000)
        break
      case 403:
        message = i18n.global.t('axios.You do not have permission to operate!')
        break
      case 404:
        message = i18n.global.t('axios.Error requesting address:') + error.response.config.url
        break
      case 408:
        message = i18n.global.t('axios.Request timed out!')
        break
      case 409:
        message = i18n.global.t('axios.The same data already exists in the system!')
        break
      case 500:
        if (error.response.msg) {
          message = error.response.msg
        } else {
          message = i18n.global.t('axios.Server internal error!')
        }
        break
      case 501:
        message = i18n.global.t('axios.Service not implemented!')
        break
      case 502:
        message = i18n.global.t('axios.Gateway error!')
        break
      case 503:
        message = i18n.global.t('axios.Service unavailable!')
        break
      case 504:
        message = i18n.global.t('axios.The service is temporarily unavailable Please try again later!')
        break
      case 505:
        message = i18n.global.t('axios.HTTP version is not supported!')
        break
      default:
        message = i18n.global.t('axios.Abnormal problem, please contact the website administrator!')
        break
    }
  }
  if (error.message?.includes('timeout')) message = i18n.global.t('axios.Network request timeout!')
  if (error.message?.includes('Network'))
    message = window.navigator.onLine
      ? i18n.global.t('axios.Server exception!')
      : i18n.global.t('axios.You are disconnected!')

  message && ElNotification({
    type: 'error',
    message
  })
}

/**
 * 关闭Loading层实例
 */
function closeLoading (options: Options) {
  if (options.loading && loadingInstance.count > 0) loadingInstance.count--
  if (loadingInstance.count === 0) {
    loadingInstance.target.close()
    loadingInstance.target = null
  }
}

/**
 * 根据请求方法组装请求数据/参数
 */
export function requestPayload (method: Method, data: anyObj) {
  if (method == 'GET') {
    return {
      params: data
    }
  } else if (method == 'POST') {
    return {
      data: data
    }
  }
}

/**
 * 下载通用文件
 * @param url
 * @param filename
 * @param config 配置项 { useFileNameTimestamp: 是否使用文件名后缀增加timestamp, 默认为true }
 */
export function downloadCommonFile (url: string, filename: string, config: { useFileNameTimestamp?: boolean } = { useFileNameTimestamp: true }) {
  return new Promise((resolve, reject) => {
    axios.get(url, { responseType: 'blob' })
      .then(res => {
        resolve(res.data)
        downloadFileStream(res.data, filename, config)
      })
      .catch(() => {
        reject(null)
      })
  })
}

/**
 * 下载瓴西文件
 * @param sourcePath
 * @param filename 文件名 当不传且res为标准返回值(headers: anyObj, data: any)时取响应头
 * @param config 配置项 { useFileNameTimestamp: 是否使用文件名后缀增加timestamp, 默认为true }
 */
export function downloadPharmacyFile (
  sourcePath: string,
  filename?: string,
  config: { useFileNameTimestamp?: boolean } = { useFileNameTimestamp: true }) {
  return new Promise((resolve, reject) => {
    downloadFile(sourcePath)
      .then(res => {
        resolve(res)
        downloadFileStream(res, filename, config)
      })
      .catch(() => {
        reject(null)
      })
  })
}

interface LoadingInstance {
  target: any
  count: number
}

interface Options {
  // 是否开启取消重复请求, 默认为 true
  cancelDuplicateRequest?: boolean
  // 是否开启loading层效果, 默认为false
  loading?: boolean
  // 是否开启简洁的数据结构响应, 默认为true
  reductDataFormat?: boolean
  // 是否开启接口错误信息展示,默认为true
  showErrorMessage?: boolean
  // 是否开启code不为0时的信息提示, 默认为true
  showCodeMessage?: boolean
  // 是否开启code为0时的信息提示, 默认为false
  showSuccessMessage?: boolean
  // 当前请求使用另外的用户token
  anotherToken?: string
}
